'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Lesson, P, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/transformers'

const TinyGPTTrainer = dynamic(() => import('../_components/widgets/TransformerWidgets').then(m => m.TinyGPTTrainer), { ssr: false })

export default function TinyGpt() {
  return (
    <Lesson>
      <Step>
        <H>A real transformer, in your tab</H>
        <P>
          Below is a complete GPT-style language model: token and position embeddings, two transformer blocks with four
          attention heads each, layer norms, residual connections, GELU MLPs, and a softmax over characters. About 60,000
          parameters. It’s built entirely from the <C>Tensor</C> engine in this course, and it trains on a small collection of
          simple stories — on your device, in a background thread.
        </P>
        <P>
          Press <strong>Start training</strong> and watch. At first it writes noise. Within seconds it discovers spaces and
          common letters. Then short words. Then whole phrases.
        </P>
        <Figure n="9.6" wide caption="Train a tiny GPT. A new sample is written every few seconds from your prompt. Below the sample, explore what each attention head has learned to look at.">
          <TinyGPTTrainer />
        </Figure>
      </Step>

      <Step gate={['q-memorise', 'q-heads2']}>
        <H>What you’re seeing</H>
        <ul className="gd-list">
          <li>
            <strong>Loss starts near 3.3</strong> — that’s <C>ln(28)</C>, uniform guessing over the 28 characters. Sound familiar?
          </li>
          <li>
            <strong>Around step 100–300</strong>, spaces and common short words (“the”, “and”) appear: the model has learned
            letter statistics.
          </li>
          <li>
            <strong>By a thousand steps</strong> it writes grammatical-looking sentences in the style of the stories.
          </li>
          <li>
            <strong>Eventually the loss gets very low</strong> — and the samples start reproducing the training stories word for
            word.
          </li>
        </ul>
        <Quiz
          id="q-memorise"
          question="After a few thousand steps, the model writes the training stories almost verbatim. Why?"
          options={[
            { label: 'The dataset is tiny (a few thousand characters), so the model can memorise it — classic overfitting.', correct: true },
            { label: 'Transformers always copy their training data.', why: 'With enough varied data they generalise — but this corpus is very small.' },
            { label: 'The temperature is too high.', why: 'Temperature affects randomness of sampling, not what the model has memorised.' },
          ]}
          explain="Real models are trained on trillions of tokens — far more than they could ever memorise — so the cheapest way to lower the loss is to learn grammar, facts and reasoning patterns instead."
        />
        <Quiz
          id="q-heads2"
          question="In the attention view, some heads mostly look at the previous one or two characters. What might such a head be doing?"
          options={[
            { label: 'Tracking local spelling patterns — which letters tend to follow which.', correct: true },
            { label: 'Nothing useful; only long-range heads matter.', why: 'Short-range heads are very useful — spelling is local.' },
            { label: 'Memorising the whole text.', why: 'Memorisation lives in all the weights together, not one attention pattern.' },
          ]}
        />
      </Step>

      <Step gate="attention-head">
        <H>Build an attention head</H>
        <P>
          Here is the single most important function in the model, with <C>Tensor</C> so gradients flow through it. The checks
          also verify the two properties that make it a <em>causal</em> head: the first token can only see itself, and changing
          the future can’t change the past.
        </P>
        <Exercise
          id="attention-head"
          title="A causal self-attention head"
          {...EX.attentionHead}
          hints={[
            <>
              <C>const q = x.matmul(wq)</C> (same for k and v), and <C>const hs = wq.cols</C>.
            </>,
            <>
              <C>q.matmul(k.transpose()).mul(1 / Math.sqrt(hs)).maskCausal().softmax()</C>, then <C>.matmul(v)</C>.
            </>,
          ]}
        />
      </Step>

      <Step>
        <H>The whole thing, in your editor</H>
        <P>
          This is the complete model and training loop — every line of it. It’s the same architecture as above (a bit smaller
          batch) and takes about 20 seconds to run. Read it top to bottom: you know what every single line does now.
        </P>
        <Exercise id="gpt-full" title="A GPT from scratch" {...EX.gptFull} timeoutMs={90000} outputHeight={480} />
        <Callout kind="why">
          The difference between this and a frontier model is scale: thousands of times wider, dozens of times deeper, a
          vocabulary of ~100,000 tokens instead of 28 characters, and trillions of tokens of training data on thousands of
          GPUs. The ideas are the ones on this page.
        </Callout>
      </Step>
    </Lesson>
  )
}
